import { useState, useEffect } from 'react'
import { useWizardStore } from '../../store/wizardStore'
import { Button } from '../ui/Button'
import { CardHeader, CardTitle, CardContent } from '../ui/Card'
import { api } from '../../lib/api'
import { clsx } from 'clsx'

export default function Step7TokenPurchase() {
  const { nextStep, previousStep, setStep, tokenQuantity, setTokenQuantity, orderId, wantsTokensOnly, wantsGifts, wantsSanta } = useWizardStore()
  // Nothing else in the basket (tokens-only, or Santa/gifts dropped): tokens become the order, so the minimum applies
  const mustBuyTokens = !(wantsGifts || wantsSanta)
  const [quantity, setQuantity] = useState(tokenQuantity || (mustBuyTokens ? 2 : 0))
  const [tokenPrice, setTokenPrice] = useState<number>(0.50)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingPrice, setIsFetchingPrice] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Calculate minimum tokens needed based on order type
  const hasOtherItems = wantsGifts || wantsSanta
  const minimumTokens = hasOtherItems ? 0 : 2

  useEffect(() => {
    fetchTokenPrice()
  }, [])

  const fetchTokenPrice = async () => {
    setIsFetchingPrice(true)
    try {
      const pricing = await api.getPricing()
      if (pricing.tokens !== undefined) {
        setTokenPrice(pricing.tokens)
      }
    } catch (err) {
      console.error('Failed to fetch token pricing:', err)
      // Keep default price of 0.50 if fetch fails
    } finally {
      setIsFetchingPrice(false)
    }
  }

  const handleIncrement = () => {
    setQuantity(prev => prev + 1)
  }

  const handleDecrement = () => {
    if (quantity > minimumTokens) {
      setQuantity(prev => prev - 1)
    }
  }

  const calculateTotal = () => {
    return (quantity * tokenPrice).toFixed(2)
  }

  const handleAddToBasket = async () => {
    if (!orderId) {
      setError('Order ID is missing. Please try again.')
      return
    }

    // Check minimum tokens for tokens-only orders
    if (mustBuyTokens && quantity < minimumTokens) {
      setError(`Minimum purchase is ${minimumTokens} tokens (£${(minimumTokens * tokenPrice).toFixed(2)}) to meet the £1 minimum spend requirement.`)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      await api.updateTokenQuantity(orderId, quantity)
      setTokenQuantity(quantity)
      nextStep()
    } catch (err) {
      console.error('Failed to save token quantity:', err)
      setError('Failed to save tokens. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleSkip = () => {
    // Can only skip if not tokens-only order
    if (!mustBuyTokens) {
      setQuantity(0)
      setTokenQuantity(0)
      nextStep()
    }
  }

  const handleBack = () => {
    if (wantsTokensOnly) {
      // If tokens-only, go back to gift decision step
      setStep(2)
    } else if (!wantsSanta) {
      // No Santa details step to return to
      setStep(wantsGifts ? 4 : 5)
    } else {
      previousStep()
    }
  }

  return (
    <>
      <CardHeader>
        <CardTitle>Activity Tokens</CardTitle>
        <p className="text-gray-600 mt-2">
          Tokens can be used to play games, use the tombola, and purchase food at the Christmas Fayre. Each token costs £{tokenPrice.toFixed(2)}.
          {!mustBuyTokens && ' Buy as many as you need now, or skip this step and buy them on the night from our friendly elves!'}
          {mustBuyTokens && (
            <span className="block mt-2 text-primary-800 font-medium">
              Minimum purchase: {minimumTokens} tokens (£{(minimumTokens * tokenPrice).toFixed(2)})
            </span>
          )}
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {error && (
            <div role="alert" className="bg-primary-50 border border-primary-300 p-4 rounded-lg">
              <p className="text-primary-800">{error}</p>
            </div>
          )}

          {/* Token Counter */}
          <div className="bg-white border-2 border-gray-200 rounded-lg p-6">
            <div className="text-center space-y-4">
              <p id="token-count-label" className="text-sm text-gray-600 font-medium">
                How many tokens would you like to purchase?
              </p>

              <div className="flex items-center justify-center gap-4" role="group" aria-labelledby="token-count-label">
                <button
                  onClick={handleDecrement}
                  disabled={quantity <= minimumTokens || isLoading || isFetchingPrice}
                  className={clsx(
                    'w-12 h-12 rounded-full border-2 flex items-center justify-center transition-all',
                    'active:scale-95 motion-reduce:active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed',
                    quantity <= minimumTokens
                      ? 'border-gray-300 text-gray-400'
                      : 'border-accent-800 bg-accent-50 text-accent-900 hover:bg-accent'
                  )}
                  aria-label="Decrease quantity"
                >
                  <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                  </svg>
                </button>

                <div className="min-w-[80px]">
                  <p className="text-5xl font-bold text-gray-900" aria-live="polite">{quantity}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {quantity === 1 ? 'token' : 'tokens'}
                  </p>
                </div>

                <button
                  onClick={handleIncrement}
                  disabled={isLoading || isFetchingPrice}
                  className="w-12 h-12 rounded-full border-2 border-accent-800 bg-accent-50 text-accent-900 hover:bg-accent flex items-center justify-center transition-all active:scale-95 motion-reduce:active:scale-100 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Increase quantity"
                >
                  <svg aria-hidden="true" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 4v16m8-8H4" />
                  </svg>
                </button>
              </div>

              {/* Running Total */}
              <div className="pt-4 border-t-2 border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">Total:</span>
                  <span className="text-3xl font-bold text-secondary-foreground">
                    £{calculateTotal()}
                  </span>
                </div>
                {quantity > 0 && (
                  <p className="text-xs text-gray-500 mt-2 text-right">
                    {quantity} × £{tokenPrice.toFixed(2)} each
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="pt-4 space-y-2">
            <Button
              onClick={handleAddToBasket}
              fullWidth
              size="lg"
              disabled={isLoading || isFetchingPrice || (mustBuyTokens && quantity < minimumTokens)}
            >
              {isLoading ? 'Saving...' : quantity > 0 ? 'Add to Basket' : 'Continue without Tokens'}
            </Button>
            {!mustBuyTokens && (
              <Button
                onClick={handleSkip}
                fullWidth
                variant="outline"
                disabled={isLoading}
              >
                Skip
              </Button>
            )}
            <Button
              onClick={handleBack}
              fullWidth
              variant="ghost"
            >
              Back
            </Button>
          </div>
        </div>
      </CardContent>
    </>
  )
}
